import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

export interface Customer {
  customer_id: string;
  name: string;
  email: string;
  phone: string | null;
  notes?: string | null;
  created_at?: string;
  total_order_value?: number;
}

interface CustomerEditModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: (updatedCustomer: { name: string; email: string; phone: string | null }) => void;
}

const CustomerEditModal = ({ customer, isOpen, onClose, onSaveSuccess }: CustomerEditModalProps) => {
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (customer) {
      setEditForm({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
      });
      setNameError(null);
      setEmailError(null);
      setPhoneError(null);
    }
  }, [customer]);

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email (e.g. you@gmail.com)";
    return null;
  };

  const handleSave = async () => {
    if (!customer) return;

    // Validate name
    if (!editForm.name.trim()) {
      setNameError("Name is required");
      toast.error("Name cannot be empty");
      return;
    }

    // Validate email
    const emailErr = validateEmail(editForm.email);
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }

    // Validate phone
    if (editForm.phone && editForm.phone.trim() !== "") {
      if (!isValidPhoneNumber(editForm.phone)) {
        setPhoneError("Enter a valid phone number");
        toast.error("Enter a valid phone number");
        return;
      }
    }

    setLoading(true);
    const { error } = await supabase
      .from("customers")
      .update({
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone || null,
      })
      .eq("customer_id", customer.customer_id);

    setLoading(false);
    if (error) {
      toast.error("Failed to update customer");
    } else {
      toast.success("Customer updated");
      onSaveSuccess({
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone || null,
      });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="customer-name" className={nameError ? "text-destructive" : ""}>Name</Label>
            <Input
              id="customer-name"
              value={editForm.name}
              onChange={(e) => {
                setEditForm({ ...editForm, name: e.target.value });
                if (nameError) setNameError(null);
              }}
              onBlur={() => {
                if (!editForm.name.trim()) setNameError("Name is required");
              }}
              placeholder="Customer name"
              className={nameError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {nameError && <p className="text-[10px] text-destructive mt-1">{nameError}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="customer-email" className={emailError ? "text-destructive" : ""}>Email</Label>
            <Input
              id="customer-email"
              type="email"
              value={editForm.email}
              onChange={(e) => {
                setEditForm({ ...editForm, email: e.target.value });
                if (emailError) setEmailError(null);
              }}
              onBlur={() => {
                if (editForm.email.trim()) {
                  setEmailError(validateEmail(editForm.email));
                }
              }}
              placeholder="you@example.com"
              className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
            />
            {emailError && <p className="text-[10px] text-destructive mt-1">{emailError}</p>}
          </div>

          <div className="space-y-2">
            <Label className={phoneError ? "text-destructive" : ""}>Phone number <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <PhoneInput
              international
              defaultCountry="SG"
              placeholder="+65 8123 4567"
              value={editForm.phone || ""}
              onChange={(val) => {
                setEditForm({ ...editForm, phone: val || "" });
                if (phoneError) {
                  if (!val || isValidPhoneNumber(val)) setPhoneError(null);
                }
              }}
              onBlur={() => {
                if (editForm.phone && editForm.phone.trim() !== "" && !isValidPhoneNumber(editForm.phone)) {
                  setPhoneError("Enter a valid phone number");
                }
              }}
              className={cn("phone-input-onboarding", phoneError && "[&>input]:border-destructive [&>input]:focus-visible:ring-destructive")}
            />
            {phoneError && <p className="text-[10px] text-destructive mt-1">{phoneError}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerEditModal;
